import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import {
  Box,
  Button,
  Card,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Select from "@mui/material/Select";
import { ClearIcon } from "@mui/x-date-pickers";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ConfirmDelete from "../../shared/components/ConfirmDelete";
import CustomDataGrid from "../../shared/components/CustomDataGrid";
import StatisticCard from "../../shared/components/StatisticCard";
import { getInvoiceStatusIcon } from "../../shared/functions/getInvoiceStatusIcon";
import { handleError } from "../../shared/functions/handleError";
import { usePagination } from "../../shared/hooks/usePagination";
import {
  IInvoice,
  IInvoiceStatistics,
} from "../../shared/interfaces/invoice/IInvoice";
import { RootState } from "../../store/store";
import InvoiceStatisticsSkeleton from "./components/InvoiceStatisticsSkeleton";
import { useInvoiceColumns } from "./hooks/useInvoiceColumns";
import { AdminInvoiceService } from "./service";

const AdminInvoice = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { invoiceStatus } = useSelector((state: RootState) => state.enums);
  const [data, setData] = useState<IInvoice[] | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<undefined | IInvoice>(
    undefined,
  );
  const [statistics, setStatistics] = useState<IInvoiceStatistics | undefined>(
    undefined,
  );
  const columns = useInvoiceColumns(setSelectedInvoice, setShowDeleteModal);
  const {
    pagination,
    count,
    setPagination,
    totalPages,
    setTotalPages,
    handleUpdateList,
    setCount,
    changeFilterHandler,
  } = usePagination({
    perPage: 30,
  });

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedInvoice(undefined);
  };
  const deleteHandler = (id?: number) => {
    !!id &&
      AdminInvoiceService.delete(id)
        .then((response) => {
          if (response) {
            handleUpdateList();
            enqueueSnackbar(
              t("toasts.successfullyDeleted", {
                title: selectedInvoice?.invoiceNumber,
              }),
              {
                variant: "success",
              },
            );
            closeDeleteModal();
          }
        })
        .catch(handleError);
  };

  useEffect(() => {
    AdminInvoiceService.get({ ...pagination, page: pagination?.page + 1 })
      .then((response) => {
        setCount(response?.count);
        setStatistics(
          response?.countsByStatus as unknown as IInvoiceStatistics,
        );
        setTotalPages(Math.ceil(response?.count / response?.perPage));
        setData(response.data);
      })
      .catch(handleError);
  }, [pagination, setTotalPages, setCount]);

  return (
    <>
      {statistics === undefined ? (
        <InvoiceStatisticsSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ mb: "20px" }}>
          {Object.keys(statistics)?.map((statistic) => {
            return (
              <StatisticCard
                key={statistic}
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
                getTitle={(item) => t(`invoice.status.${item}`)}
                getIcon={(item) =>
                  getInvoiceStatusIcon(item as keyof IInvoiceStatistics)
                }
                title={statistic}
                getValue={(item) =>
                  String(statistics[item as keyof typeof statistics])
                }
              />
            );
          })}
        </Grid>
      )}
      <Card>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            paddingBottom: "20px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              width: {
                xs: "100%",
                md: "50%",
              },
            }}
          >
            <TextField
              onChange={(e) => {
                changeFilterHandler("invoiceNumber", e.target.value);
              }}
              label={t("general.searchByInvoiceNumber")}
              sx={{
                width: { xs: "100%", md: "50%" },
                paddingBottom: { xs: 2, md: 0 },
                marginRight: { md: 2 },
              }}
            />

            <FormControl
              sx={{
                width: { xs: "100%", md: "50%" },
                paddingBottom: { xs: 2, md: 0 },
              }}
            >
              <InputLabel>{t("general.status")}</InputLabel>
              <Select
                labelId={`status-label`}
                id={`value-select`}
                value={pagination?.status}
                label={t("general.status")}
                endAdornment={
                  pagination?.status && (
                    <InputAdornment sx={{ marginRight: "10px" }} position="end">
                      <IconButton
                        onClick={() => {
                          changeFilterHandler("status", null);
                        }}
                      >
                        <ClearIcon></ClearIcon>
                      </IconButton>
                    </InputAdornment>
                  )
                }
                onChange={(e) => {
                  changeFilterHandler("status", e.target.value);
                }}
              >
                {invoiceStatus?.map((status) => {
                  return (
                    <MenuItem key={status} value={status}>
                      {t(`invoice.status.${status}`)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          <Button
            variant={"contained"}
            onClick={() => navigate("/invoice/create")}
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            <AddCircleOutlineOutlinedIcon sx={{ marginRight: "5px" }} />
            {t("button.addNewInvoice")}
          </Button>
        </Box>
        <CustomDataGrid
          columns={columns}
          rows={data || []}
          rowCount={count}
          setPagination={setPagination}
          pagination={pagination}
          loading={data === undefined}
          hasNextPage={totalPages < pagination?.page}
        />

        <ConfirmDelete
          open={showDeleteModal}
          title={selectedInvoice?.invoiceNumber}
          handleClose={closeDeleteModal}
          handleSubmit={() => deleteHandler(selectedInvoice?.id)}
        />
      </Card>
    </>
  );
};

export default AdminInvoice;
